import React, { useEffect } from "react";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  orderSummaryRequest,
  rescheduleLabTestRequest,
} from "src/redux/slices/labTestSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import Loader from "src/components/common/Loader";
import { SampleCollectionDetails, TestSummary } from "../LabTestSummaryScreen";
import Card from "src/components/app/Card/Card";
import TestDetailsInCart from "src/components/app/lab-tests/TestDetailsInCart";
import BillingView from "src/components/app/lab-tests/BillingView";
import Button from "src/components/app/Button";
import moment from "moment";
import { toast } from "sonner";

const RescheduleSummaryScreen: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const location = useLocation();
  const orderSummaryState = useAppSelector(
    (state) => state.labTest.orderSummary
  );
  const dispatch = useAppDispatch();
  const contactNo = useAppSelector(
    (state) => state.auth.patient.data?.contact_no
  );
  const countryCode = useAppSelector(
    (state) => state.auth.patient.data?.country_code
  );
  const isRescheduleLoading = useAppSelector(
    (state) => state.labTest.rescheduleLabTest.isLoading
  );
  useEffect(() => {
    const payload = {
      order_master_id: location.state?.orderDetails?.order_master_id,
    };
    new Promise((resolve, reject) => {
      dispatch(orderSummaryRequest({ resolve, reject, payload }));
    });
  }, []);

  const rescheduleLabTest = () => {
    const payload = {
      appointment_date: moment(location?.state?.selectedDate).format(
        "YYYY-MM-DD"
      ),
      collection_slot_id: location?.state?.selectedTimeSlotId,
      order_master_id: location?.state?.orderDetails?.order_master_id,
      remark: "Rescheduled", // ← Selected reason from step 4
      slot_time: location?.state?.selectedTimeSlot,
    };
    new Promise((resolve, reject) => {
      dispatch(rescheduleLabTestRequest({ resolve, reject, payload }));
    })
      .then(() => {
        navigate(`/app/${supportProgramKey}/book-lab-test/congratulation`, {
          state: {
            order_master_id: location?.state?.orderDetails?.order_master_id,
          },
        });
        toast.success("Lab test rescheduled successfully");
      })
      .catch((error) => {
        toast.error("Something went wrong");
      });
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <Header
        title="Lab Test Summary"
        titlePosition="left"
        onBackClick={() => {
          navigate(`/app/${supportProgramKey}/book-lab-test/select-time-slot`, {
            state: {
              ...location.state,
              selectedDate: null,
              selectedTimeSlot: null,
              selectedTimeSlotId: null,
            },
          });
        }}
      />
      {orderSummaryState?.isLoading ? (
        <Loader />
      ) : (
        <div className="flex flex-col gap-4 p-4 overflow-y-auto h-[calc(100vh-140px)]">
          <TestSummary
            patientMemberName={orderSummaryState?.data?.member?.name || ""}
            address={orderSummaryState?.data?.address?.address || ""}
            email={orderSummaryState?.data?.member?.email || ""}
            phone={`${contactNo}`}
          />
          <SampleCollectionDetails
            date={location?.state?.selectedDate}
            timeSlot={location?.state?.selectedTimeSlot}
          />
          <div>
            <h2 className="text-md font-semibold mb-2">Test Details</h2>
            <Card stackTitleAndDescription={true}>
              <TestDetailsInCart
                testListInCart={
                  orderSummaryState?.data?.items?.map((item) => ({
                    name: item.data.name,
                    price: item.data.price,
                    lab_test_id: item.lab_test_id,
                    code: item.test_code,
                  })) || []
                }
                showAddMoreTest={false}
                disableDeleteTest={true}
                disableAddMoreTest={true}
              />
            </Card>
          </div>
          <div>
            <h2 className="text-md font-semibold mb-2">Billing</h2>
            <Card stackTitleAndDescription={true}>
              <BillingView
                billingTitle="Item Total"
                discountTitle="Discount on Item(s)"
                orderTotal={
                  orderSummaryState?.data?.order_total?.toString() || "0"
                }
                homeCollectionCharge={
                  orderSummaryState?.data?.home_collection_charge || 0
                }
                serviceCharge={orderSummaryState?.data?.service_charge || 0}
                finalPayableAmount={
                  orderSummaryState?.data?.final_payable_amount || 0
                }
                discount={
                  Number(orderSummaryState?.data?.order_total) -
                  Number(orderSummaryState?.data?.payable_amount)
                }
                couponDiscount={Number(
                  orderSummaryState?.data?.coupon_discount || 0
                )}
              />
            </Card>
          </div>
        </div>
      )}
      <div className="fixed bottom-0 flex items-center justify-center left-0 right-0 bg-white p-4 border-t border-gray-200">
        <Button
          size="lg"
          fullWidth
          onClick={() => {
            rescheduleLabTest();
          }}
          disabled={isRescheduleLoading}
          loading={isRescheduleLoading}
        >
          Reschedule
        </Button>
      </div>
    </Layout>
  );
};

export default RescheduleSummaryScreen;
